import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Section } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { useWs } from "@/context/WorkspaceContext";
import { api, download, errMsg } from "@/lib/api";

function MonthlyToggle() {
  const { user, refresh } = useAuth();
  const on = user?.notify_monthly !== false;
  const save = async (v) => {
    try { await api.put("/me/notifications/monthly", { enabled: v }); await refresh(); toast.success("Preferência salva"); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    try { const { data } = await api.post("/me/notifications/monthly-test"); toast.success(`Resumo de ${data.month.slice(5)}/${data.month.slice(0, 4)} enviado para ${data.email}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-6 border-b border-[#1E3A6E] pb-5" data-testid="monthly-summary-row">
      <div className="flex items-center justify-between gap-4">
        <div><div className="font-medium">Resumo mensal (Pessoa Física e Família)</div><p className="text-xs text-slate-400">Todo dia 1º às 8h (Brasília): receitas, despesas, saldo, maiores gastos e patrimônio do mês anterior.</p></div>
        <Switch checked={on} onCheckedChange={save} data-testid="monthly-summary-switch" />
      </div>
      <Button size="sm" variant="outline" className="mt-3 border-[#1E3A6E] bg-transparent" onClick={test} data-testid="monthly-summary-test-btn">Enviar resumo de teste</Button>
    </div>
  );
}

function Notifications() {
  const { user, refresh } = useAuth();
  const on = user?.notify_invoice !== false;
  const days = user?.notify_days || 3;
  const save = async (body) => {
    try { await api.put("/me/notifications", { invoice_reminders: on, days_before: days, ...body }); await refresh(); toast.success("Preferência salva"); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    try { const { data } = await api.post("/me/notifications/test"); toast.success(`E-mail de teste enviado para ${data.email}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title="Avisos por e-mail" testid="notifications-section">
      <MonthlyToggle />
      <div className="flex items-center justify-between gap-4">
        <div><div className="font-medium">Vencimento de faturas do cartão</div><p className="text-xs text-slate-400">E-mail diário às 8h (Brasília) com as faturas em aberto que vencem em breve e o total a pagar.</p></div>
        <Switch checked={on} onCheckedChange={(v) => save({ invoice_reminders: v })} data-testid="invoice-reminder-switch" />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-slate-400">Avisar com</span>
        <select value={days} disabled={!on} onChange={(e) => save({ days_before: Number(e.target.value) })} className="h-9 rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-2" data-testid="reminder-days-select">
          {[1, 2, 3, 5, 7, 10].map((n) => <option key={n} value={n}>{`${n} dia(s)`}</option>)}
        </select>
        <span className="text-slate-400">de antecedência</span>
        <Button size="sm" variant="outline" className="ml-auto border-[#1E3A6E] bg-transparent" onClick={test} data-testid="reminder-test-btn">Enviar e-mail de teste</Button>
      </div>
    </Section>
  );
}

function Members() {
  const { wid, ws } = useWs();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const load = () => api.get(`/workspaces/${wid}/members`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [wid]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post(`/workspaces/${wid}/members`, { email, role }); setEmail(""); toast.success("Membro adicionado"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (uid) => { await api.delete(`/workspaces/${wid}/members/${uid}`); load(); };
  if (!data) return null;
  return (
    <Section title={`Membros do espaço · ${ws?.name}`} testid="members-section">
      {data.my_role === "owner" ? (
        <form onSubmit={add} className="mb-4 flex flex-wrap gap-2">
          <Input type="email" placeholder="email@exemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="max-w-xs bg-[#0B1A33]" data-testid="member-email-input" />
          <select value={role} onChange={(e) => setRole(e.target.value)} className="h-10 rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-3 text-sm" data-testid="member-role-select">
            <option value="editor">Editor</option><option value="viewer">Somente leitura</option>
          </select>
          <Button type="submit" data-testid="member-add-btn"><UserPlus className="mr-1 h-4 w-4" />Convidar</Button>
        </form>
      ) : <p className="mb-3 text-sm text-slate-400">Seu papel neste espaço: {data.my_role}</p>}
      {data.members.map((m) => (
        <div key={m.user_id} className="flex items-center justify-between border-b border-[#1E3A6E]/50 py-2 text-sm" data-testid="member-row">
          <span>{m.email} · {m.role === "viewer" ? "Somente leitura" : "Editor"}</span>
          {data.my_role === "owner" && <button onClick={() => remove(m.user_id)} className="text-red-300" data-testid={`member-remove-${m.user_id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      {!data.members.length && <p className="text-sm text-slate-400">Nenhum membro convidado.</p>}
    </Section>
  );
}

export default function Privacidade() {
  const { user, refresh, signOut } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const toggleAi = async (v) => { await api.put("/me/consents", { ai_consent: v }); await refresh(); toast.success(v ? "Consentimento concedido" : "Consentimento revogado"); };
  const del = async () => {
    if (!window.confirm("Esta ação exclui DEFINITIVAMENTE sua conta e todos os dados. Continuar?")) return;
    try { await api.post("/me/delete", { password: pw }); await signOut(); toast.success("Conta excluída"); nav("/"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="privacidade-page">
      <PageHeader eyebrow="LGPD" title="Privacidade e controle dos dados" subtitle="Seus direitos como titular: consentimento, portabilidade, compartilhamento e exclusão." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Consentimentos">
          <div className="flex items-center justify-between gap-4">
            <div><div className="font-medium">Mentor IA</div><p className="text-xs text-slate-400">Envio de resumo calculado dos seus números ao provedor de IA para gerar respostas.</p></div>
            <Switch checked={!!user?.ai_consent} onCheckedChange={toggleAi} data-testid="ai-consent-switch" />
          </div>
          <p className="mt-4 text-xs text-slate-500">Termos e Política aceitos em {user?.consents?.terms ? new Date(user.consents.terms).toLocaleString("pt-BR") : "—"} (versão {user?.consents?.version}).</p>
        </Section>
        <Section title="Portabilidade">
          <p className="text-sm text-slate-400">Baixe todos os seus dados pessoais e financeiros em formato JSON estruturado.</p>
          <Button className="mt-4" onClick={() => download("/me/export", "prospera-meus-dados.json")} data-testid="export-data-btn"><Download className="mr-1 h-4 w-4" />Exportar meus dados</Button>
        </Section>
        <div className="lg:col-span-2"><Notifications /></div>
        <div className="lg:col-span-2"><Members /></div>
        <Section title="Excluir conta" className="lg:col-span-2 !border-red-500/30" testid="delete-account-section">
          <p className="text-sm text-slate-400">Remove permanentemente a conta, espaços próprios, lançamentos, histórico do Mentor IA e cancela assinaturas.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Input type="password" placeholder="Confirme sua senha" value={pw} onChange={(e) => setPw(e.target.value)} className="max-w-xs bg-[#0B1A33]" data-testid="delete-password-input" />
            <Button variant="destructive" disabled={!pw} onClick={del} data-testid="delete-account-btn"><Trash2 className="mr-1 h-4 w-4" />Excluir definitivamente</Button>
          </div>
        </Section>
      </div>
    </div>
  );
}
